import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import PATH_PAGE from '@/router/path';
import { RouteParams } from '@/router/type';
import { useBlueprintStore } from '@/store/blueprint';
import { useNavigate, useParams } from 'react-router-dom';
import { useState, useMemo } from 'react';
import { SearchIcon, BoxIcon, TagIcon, CheckCircle2Icon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useGetServerlessProjects } from '@/service/serverless-gpu/hook';
import { useServerlessStore } from '@/store/serverless';
import { getBlueprintImages } from '@/utils/blueprint';

const ServerlessBlueprintOverview = () => {
  const { orgId, workspaceId } = useParams<RouteParams>();
  const { data } = useBlueprintStore();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Get user's serverless projects
  const { token } = useServerlessStore();
  const { data: projectsData } = useGetServerlessProjects({
    enabled: !!token,
  });

  const handleClick = (name: string) => {
    navigate(
      PATH_PAGE.dashboard.serverlessGPU.blueprint(orgId!, workspaceId!).byName(name)
    );
  };

  // Check if a blueprint has been deployed (only active projects)
  const isBlueprintDeployed = (blueprintName: string, category?: string) => {
    if (!projectsData?.projects) return false;

    // The project naming pattern from deployment is: [Blueprint][{category}] {blueprintName}
    const blueprintProjectPattern = `[Blueprint][${category}] ${blueprintName}`;

    return projectsData.projects.some(
      (project) => project.project_name === blueprintProjectPattern && project.status === 'active'
    );
  };

  // Get all unique categories
  const categories = useMemo(() => {
    if (!data?.productionBluePrint) return [];
    const cats = new Set(
      data.productionBluePrint
        .map((item) => item.category)
        .filter(Boolean) as string[]
    );
    return Array.from(cats);
  }, [data]);

  // Filter blueprints based on search and category
  const filteredBlueprints = useMemo(() => {
    if (!data?.productionBluePrint) return [];

    return data.productionBluePrint.filter((item) => {
      const matchesSearch =
        !searchQuery ||
        item.bluePrintName.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.description?.toLowerCase().includes(searchQuery.toLowerCase());

      const matchesCategory =
        !selectedCategory || item.category === selectedCategory;

      return matchesSearch && matchesCategory;
    });
  }, [data, searchQuery, selectedCategory]);

  return (
    <div className="container mt-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Blueprint</h1>
            <p className="mt-1 text-sm text-gray-500">
              Ready-to-use templates to kickstart your AI projects
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="text-sm">
              {filteredBlueprints.length} Templates
            </Badge>
          </div>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex-1 lg:max-w-md">
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search blueprints..."
            startIcon={<SearchIcon className="h-4 w-4 text-gray-400" />}
          />
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-gray-600">Category:</span>
          <Button
            variant={selectedCategory === null ? 'primary' : 'outline'}
            size="sm"
            onClick={() => setSelectedCategory(null)}
          >
            All
          </Button>
          {categories.map((category) => (
            <Button
              key={category}
              variant={selectedCategory === category ? 'primary' : 'outline'}
              size="sm"
              onClick={() => setSelectedCategory(category)}
            >
              {category}
            </Button>
          ))}
        </div>
      </div>

      {/* Blueprint Cards Grid */}
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {filteredBlueprints.map((item) => {
          const images = getBlueprintImages(item.bluePrintName);
          const hasBanner = images.banner !== null;
          const hasFavIcon = images.favIcon !== null;

          return (
            <Card
              key={item.bluePrintName}
              onClick={() => handleClick(item.bluePrintName)}
              className="group cursor-pointer overflow-hidden transition-all hover:shadow-xl"
            >
              {/* Card Header with Banner/Icon */}
              <div
                className="relative bg-gradient-to-br from-primary/10 to-primary/5 p-6"
                style={
                  hasBanner
                    ? {
                        backgroundImage: `url(${images.banner})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                      }
                    : undefined
                }
              >
                {/* Overlay for better icon visibility when banner is present */}
                {hasBanner && (
                  <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
                )}

                <div className="relative flex h-12 w-12 items-center justify-center rounded-lg bg-white/90 shadow-md group-hover:bg-white">
                  {hasFavIcon ? (
                    <img
                      src={images.favIcon || undefined}
                      alt={item.bluePrintName}
                      className="h-10 w-10 rounded-lg object-contain"
                    />
                  ) : (
                    <BoxIcon className="h-6 w-6 text-primary" />
                  )}
                </div>
              </div>

              {/* Card Content */}
              <div className="p-6">
                <div className="mb-3 flex items-start justify-between gap-2">
                  <h3 className="text-lg font-semibold text-gray-900 group-hover:text-primary">
                    {item.bluePrintName}
                  </h3>
                  {item.category && (
                    <Badge variant="secondary" className="shrink-0">
                      {item.category}
                    </Badge>
                  )}
                </div>

                {item.description && (
                  <p className="mb-4 line-clamp-2 text-sm text-gray-600">
                    {item.description}
                  </p>
                )}

                {/* Tags */}
                {item.tags && item.tags.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {item.tags.map((tag) => (
                      <div
                        key={tag}
                        className="flex items-center gap-1 rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-600"
                      >
                        <TagIcon className="h-3 w-3" />
                        {tag}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Card>
          );
        })}
      </div>

      {/* Empty State */}
      {filteredBlueprints.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <BoxIcon className="mb-4 h-16 w-16 text-gray-300" />
          <h3 className="mb-2 text-lg font-semibold text-gray-900">
            No blueprints found
          </h3>
          <p className="text-sm text-gray-500">
            Try adjusting your search or filter criteria
          </p>
        </div>
      )}
    </div>
  );
};

export default ServerlessBlueprintOverview;
